import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Bell,
  CheckCircle2,
  Eye,
  Heart,
  Mail,
  MessageSquare,
  Monitor,
  Pencil,
  Search,
  Send,
  ShieldCheck,
  Smartphone,
  UserRound,
  Users,
  X,
} from 'lucide-react';
import {
  getEmailTemplates,
  getRsvpPendingAudience,
  getSmsTemplates,
  getOutboundSettings,
  previewOutboundEmail,
  previewOutboundSms,
  sendEmails,
  sendSms,
} from '../../../data/api';

export const SEND_ENTRY_CONTEXT_TYPES = {
  DASHBOARD_REPLY: 'dashboard_reply',
  GUEST_DETAIL_FOLLOW_UP: 'guest_detail_follow_up',
  GUEST_LIST_SELECTION: 'guest_list_selection',
  JOURNEY_MILESTONE: 'journey_milestone',
};

const STEPS = [
  { key: 'purpose', label: 'Type' },
  { key: 'channel', label: 'Channel' },
  { key: 'template', label: 'Template' },
  { key: 'audience', label: 'Audience' },
  { key: 'review', label: 'Review' },
];

const PURPOSES = [
  { key: 'invitation', label: 'Invitation', description: 'Prepare an invitation email for selected guests.', icon: Mail },
  { key: 'reminder', label: 'Reminder', description: 'Prepare an RSVP reminder for selected guests.', icon: Bell },
  { key: 'follow_up', label: 'Follow-up', description: 'Compose a targeted guest follow-up email.', icon: Send },
  { key: 'thank_you', label: 'Thank You', description: 'Prepare appreciation notes for selected guests.', icon: Heart },
  { key: 'custom', label: 'Custom', description: 'Use any available admin email template.', icon: Pencil },
];

const CHANNELS = [
  { key: 'email', label: 'Email', description: 'Send via email.', icon: Mail, available: true },
  { key: 'sms', label: 'SMS', description: 'Available only when SMS provider settings are enabled. Still requires preview and safety controls.', icon: MessageSquare, available: true },
];

function createCorrelationId() {
  return `comm-send-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function getContextLabel(context) {
  if (!context || !context.type) return '';
  if (context.type === SEND_ENTRY_CONTEXT_TYPES.DASHBOARD_REPLY) return 'Dashboard reply context';
  if (context.type === SEND_ENTRY_CONTEXT_TYPES.GUEST_DETAIL_FOLLOW_UP) return 'Guest follow-up context';
  if (context.type === SEND_ENTRY_CONTEXT_TYPES.GUEST_LIST_SELECTION) return 'Guest list selection context';
  if (context.type === SEND_ENTRY_CONTEXT_TYPES.JOURNEY_MILESTONE) return 'Journey milestone context';
  return 'Communications context';
}

function getGuestId(guest) {
  return String(guest?.id || guest?._id || '');
}

function getGuestName(guest) {
  return `${guest?.first_name || ''} ${guest?.last_name || ''}`.trim() || guest?.email || 'Unnamed guest';
}

function getTemplateId(template) {
  return String(template?.id || template?._id || '');
}

function getTemplateSubject(template) {
  return template?.subject || '';
}

function getTemplateBody(template) {
  return template?.html_content || template?.message_text || '';
}

function templateMatchesPurpose(template, purpose) {
  const type = String(template?.template_type || '').toLowerCase();
  const name = String(template?.name || '').toLowerCase();
  if (purpose === 'custom') return true;
  if (purpose === 'invitation') return ['invitation', 'formal_invitation', 'save_the_date'].includes(type) || name.includes('invitation');
  if (purpose === 'reminder') return type.includes('reminder') || name.includes('reminder') || name.includes('rsvp');
  if (purpose === 'follow_up') return type.includes('follow') || name.includes('follow') || type === 'custom';
  if (purpose === 'thank_you') return type === 'thank_you' || name.includes('thank');
  return true;
}

function formatMode(mode) {
  if (mode === 'hold') return 'Hold Outbound Messages';
  if (mode === 'test') return 'Test Mode';
  if (mode === 'live') return 'Live Send Enabled';
  return 'Mode unavailable';
}

function formatModeCompact(mode) {
  if (mode === 'hold') return 'Hold';
  if (mode === 'test') return 'Test';
  if (mode === 'live') return 'Live';
  return 'Unavailable';
}

function stripHtml(value = '') {
  return String(value).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

function SummaryRow({ label, value }) {
  return (
    <div className="communications-send-summary__row">
      <span>{label}</span>
      <strong>{value || '-'}</strong>
    </div>
  );
}

function templateTypeLabel(template) {
  const type = String(template?.template_type || '').replace(/_/g, ' ');
  return type ? type.replace(/\b\w/g, letter => letter.toUpperCase()) : 'Template';
}

function SelectableCard({ item, selected, disabled, onSelect, children }) {
  const Icon = item.icon;
  return (
    <button
      type="button"
      className={`communications-send-card ${selected ? 'communications-send-card--selected' : ''} ${disabled ? 'communications-send-card--disabled' : ''}`}
      onClick={() => !disabled && onSelect(item.key)}
      disabled={disabled}
      aria-pressed={selected}
    >
      {Icon && (
        <span className="communications-send-card__icon">
          <Icon size={24} />
        </span>
      )}
      <span className="communications-send-card__body">
        <strong>{item.label}</strong>
        <small>{item.description || item.note}</small>
        {children}
      </span>
      {selected && <CheckCircle2 className="communications-send-card__check" size={20} />}
    </button>
  );
}

// Cursor-tracked 3D tilt for the Step 5 preview surface. Reserved for premium
// surfaces only — gives the rendered preview the feel of a physical card the
// operator is holding. Disabled with prefers-reduced-motion.
function useTilt(ref, { max = 4 } = {}) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const reduce = typeof window !== 'undefined'
      && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return undefined;
    let frame = 0;
    let pendingX = 0;
    let pendingY = 0;
    const apply = () => {
      frame = 0;
      el.style.setProperty('--tilt-x', `${pendingX.toFixed(2)}deg`);
      el.style.setProperty('--tilt-y', `${pendingY.toFixed(2)}deg`);
    };
    const onMove = (event) => {
      const rect = el.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = (event.clientX - cx) / (rect.width / 2);
      const dy = (event.clientY - cy) / (rect.height / 2);
      pendingY = Math.max(-1, Math.min(1, dx)) * max;
      pendingX = -Math.max(-1, Math.min(1, dy)) * (max * 0.75);
      if (!frame) frame = window.requestAnimationFrame(apply);
    };
    const onLeave = () => {
      pendingX = 0;
      pendingY = 0;
      if (!frame) frame = window.requestAnimationFrame(apply);
    };
    el.addEventListener('mousemove', onMove);
    el.addEventListener('mouseleave', onLeave);
    return () => {
      el.removeEventListener('mousemove', onMove);
      el.removeEventListener('mouseleave', onLeave);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [ref, max]);
}

export default function SendPanel({ guests = [], initialContext = null, onOpenOutbox = null }) {
  const [currentStep, setCurrentStep] = useState(0);
  const step5PreviewRef = useRef(null);
  useTilt(step5PreviewRef);
  const [purpose, setPurpose] = useState(initialContext?.purpose || 'invitation');
  const [channel, setChannel] = useState(initialContext?.channel || 'email');
  const [templateId, setTemplateId] = useState(initialContext?.templateId || '');
  const [selectedGuestIds, setSelectedGuestIds] = useState(initialContext?.guestIds || []);
  const [customSubject, setCustomSubject] = useState(initialContext?.customSubject || '');
  const [customBody, setCustomBody] = useState(initialContext?.customBody || '');
  const [recipientSearch, setRecipientSearch] = useState('');
  const [audienceMode, setAudienceMode] = useState(initialContext?.audienceType || 'manual');
  const [audienceData, setAudienceData] = useState(null);
  const [audienceLoading, setAudienceLoading] = useState(false);
  const [audienceError, setAudienceError] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewViewport, setPreviewViewport] = useState('desktop');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewConfirmed, setPreviewConfirmed] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [liveConfirmOpen, setLiveConfirmOpen] = useState(false);
  const [correlationId, setCorrelationId] = useState(() => createCorrelationId());
  const [settings, setSettings] = useState(null);
  const [settingsError, setSettingsError] = useState('');
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [smsTemplates, setSmsTemplates] = useState([]);
  const [templatesError, setTemplatesError] = useState('');
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const submittingRef = useRef(false);

  const contextLabel = getContextLabel(initialContext);

  useEffect(() => {
    let active = true;
    async function loadSendData() {
      setSettingsLoading(true);
      setTemplatesLoading(true);
      try {
        const [settingsData, emailTemplateData, smsTemplateData] = await Promise.all([
          getOutboundSettings(),
          getEmailTemplates(),
          getSmsTemplates(),
        ]);
        if (!active) return;
        setSettings(settingsData);
        setSettingsError('');
        setTemplates((emailTemplateData || []).filter(template => template.template_type !== 'confirmation' && !template.is_live));
        setSmsTemplates(smsTemplateData || []);
        setTemplatesError('');
      } catch (error) {
        if (!active) return;
        setSettingsError(error.response?.data?.detail || 'Unable to load send settings.');
        setTemplatesError(error.response?.data?.detail || 'Unable to load email templates.');
      } finally {
        if (active) {
          setSettingsLoading(false);
          setTemplatesLoading(false);
        }
      }
    }
    loadSendData();
    return () => {
      active = false;
    };
  }, []);

  const selectedPurpose = PURPOSES.find(item => item.key === purpose);
  const selectedChannel = CHANNELS.find(item => item.key === channel);
  const smsAvailable = Boolean(settings?.sms_provider_enabled);
  const emailTemplates = useMemo(
    () => templates.filter(template => getTemplateId(template)),
    [templates]
  );
  const smsTemplateList = useMemo(
    () => smsTemplates.filter(template => getTemplateId(template)),
    [smsTemplates]
  );
  const activeTemplates = channel === 'sms' ? smsTemplateList : emailTemplates;
  const templatesForStep = useMemo(() => {
    const matching = activeTemplates.filter(template => templateMatchesPurpose(template, purpose));
    return matching.length ? matching : activeTemplates;
  }, [activeTemplates, purpose]);
  const selectedTemplate = useMemo(
    () => activeTemplates.find(template => getTemplateId(template) === templateId) || null,
    [activeTemplates, templateId]
  );
  const eligibleGuests = useMemo(
    () => guests.filter(guest => {
      if (!getGuestId(guest)) return false;
      if (channel === 'sms') return String(guest.phone || '').trim() && !guest.sms_opted_out;
      return String(guest.email || '').trim();
    }).sort((a, b) => getGuestName(a).localeCompare(getGuestName(b), undefined, { sensitivity: 'base' })),
    [channel, guests]
  );
  const filteredGuests = useMemo(() => {
    const query = recipientSearch.trim().toLowerCase();
    if (!query) return eligibleGuests.slice(0, 150);
    return eligibleGuests.filter(guest => {
      const name = getGuestName(guest).toLowerCase();
      const email = String(guest.email || '').toLowerCase();
      const phone = String(guest.phone || '').toLowerCase();
      return name.includes(query) || email.includes(query) || phone.includes(query);
    }).slice(0, 150);
  }, [eligibleGuests, recipientSearch]);
  const selectedGuests = useMemo(
    () => {
      const localById = new Map(guests.map(guest => [getGuestId(guest), guest]));
      const audienceById = new Map((audienceData?.eligible_guests || []).map(guest => [getGuestId(guest), guest]));
      return selectedGuestIds.map(id => localById.get(id) || audienceById.get(id)).filter(Boolean);
    },
    [audienceData, guests, selectedGuestIds]
  );
  const recipientRows = audienceMode === 'rsvp_pending' && audienceData ? selectedGuests : filteredGuests;

  const resetPreview = () => {
    setPreviewData(null);
    setPreviewConfirmed(false);
    setSubmitResult(null);
    setCorrelationId(createCorrelationId());
  };

  useEffect(() => {
    if (templateId || !templatesForStep.length) return;
    const template = templatesForStep[0];
    setTemplateId(getTemplateId(template));
    setCustomSubject(getTemplateSubject(template));
    setCustomBody(getTemplateBody(template));
    resetPreview();
  }, [templateId, templatesForStep]);

  useEffect(() => {
    const eligibleIds = new Set(eligibleGuests.map(getGuestId));
    setSelectedGuestIds(ids => ids.filter(id => eligibleIds.has(id)));
  }, [eligibleGuests]);

  useEffect(() => {
    if (initialContext?.audienceType === 'rsvp_pending') {
      loadRsvpPendingAudience();
    }
    // Initial context remounts this component through its key; avoid reloading on every state change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectTemplate = nextTemplateId => {
    const nextTemplate = activeTemplates.find(template => getTemplateId(template) === nextTemplateId);
    setTemplateId(nextTemplateId);
    setCustomSubject(getTemplateSubject(nextTemplate));
    setCustomBody(getTemplateBody(nextTemplate));
    resetPreview();
    // Auto-advance: template selected → move to audience step.
    setCurrentStep(step => (step === 2 ? 3 : step));
  };

  const handlePurposeSelect = nextPurpose => {
    setPurpose(nextPurpose);
    const matching = activeTemplates.find(template => templateMatchesPurpose(template, nextPurpose));
    if (matching) {
      setTemplateId(getTemplateId(matching));
      setCustomSubject(getTemplateSubject(matching));
      setCustomBody(getTemplateBody(matching));
    }
    setAudienceData(null);
    setAudienceError('');
    resetPreview();
    // Auto-advance: purpose selected → move to channel step.
    setCurrentStep(step => (step === 0 ? 1 : step));
  };

  const handleChannelSelect = nextChannel => {
    if (nextChannel === 'sms' && !smsAvailable) return;
    setChannel(nextChannel);
    setTemplateId('');
    setCustomSubject('');
    setCustomBody('');
    setAudienceData(null);
    setAudienceError('');
    resetPreview();
    // Auto-advance: channel selected → move to template step.
    setCurrentStep(step => (step === 1 ? 2 : step));
  };

  const toggleGuest = guestId => {
    setSelectedGuestIds(ids => (
      ids.includes(guestId) ? ids.filter(id => id !== guestId) : [...ids, guestId]
    ));
    resetPreview();
  };

  const clearRecipients = () => {
    setSelectedGuestIds([]);
    setAudienceMode('manual');
    setAudienceData(null);
    setAudienceError('');
    resetPreview();
  };

  const currentCampaignKey = initialContext?.campaignKey || `rsvp-${purpose}-${channel}`;

  const loadRsvpPendingAudience = async () => {
    setAudienceMode('rsvp_pending');
    setAudienceLoading(true);
    setAudienceError('');
    try {
      const result = await getRsvpPendingAudience({
        channel,
        campaign_key: currentCampaignKey,
      });
      setAudienceData(result);
      setSelectedGuestIds((result.eligible_guests || []).map(guest => getGuestId(guest)).filter(Boolean));
      resetPreview();
    } catch (error) {
      setAudienceError(error.response?.data?.detail || 'RSVP Pending audience could not be loaded.');
    } finally {
      setAudienceLoading(false);
    }
  };

  const updateSubject = value => {
    setCustomSubject(value);
    resetPreview();
  };

  const updateBody = value => {
    setCustomBody(value);
    resetPreview();
  };

  const canPreview = Boolean(templateId)
    && selectedGuestIds.length > 0
    && Boolean(customBody.trim())
    && (channel === 'sms' || Boolean(customSubject.trim()))
    && (channel !== 'sms' || smsAvailable);

  const submitBlockedReason = useMemo(() => {
    if (settingsLoading) return 'Send settings are still loading.';
    if (settingsError || !settings) return 'Send settings are unavailable.';
    if (!settings.safety_enabled) return 'Sending is disabled in settings.';
    if (channel === 'sms' && !settings.sms_provider_enabled) return 'SMS is disabled until the provider is configured and enabled.';
    if (!templateId) return `Select a ${channel === 'sms' ? 'text message' : 'email'} template first.`;
    if (!selectedGuestIds.length) return `Select at least one ${channel === 'sms' ? 'SMS-ready' : 'email'} recipient.`;
    if (!customBody.trim()) return 'Message body is required.';
    if (channel === 'email' && !customSubject.trim()) return 'Subject is required.';
    if (!previewConfirmed) return 'Open and confirm Preview before submitting.';
    if (!previewData?.preview_digest) return 'Preview confirmation is missing its safety digest. Re-open Preview.';
    if (submitting) return 'Submission is already in progress.';
    return '';
  }, [
    channel,
    customBody,
    customSubject,
    previewConfirmed,
    previewData?.preview_digest,
    selectedGuestIds.length,
    settings,
    settingsError,
    settingsLoading,
    submitting,
    templateId,
  ]);

  const finalButtonLabel = useMemo(() => {
    if (submitting) return 'Submitting...';
    if (submitBlockedReason) return 'Unavailable';
    if (settings?.send_mode === 'hold') return 'Queue for Review';
    if (settings?.send_mode === 'test') return 'Send Test';
    if (settings?.send_mode === 'live') return 'Send Now';
    return 'Unavailable';
  }, [settings?.send_mode, submitBlockedReason, submitting]);

  const goNext = () => setCurrentStep(step => Math.min(step + 1, STEPS.length - 1));
  const goBack = () => setCurrentStep(step => Math.max(step - 1, 0));

  const handlePreview = async ({ openModal = true } = {}) => {
    if (!canPreview) return;
    if (openModal) setPreviewOpen(true);
    setPreviewLoading(true);
    setSubmitResult(null);
    try {
      const payload = {
        template_id: templateId,
        guest_ids: selectedGuestIds,
        custom_body: customBody,
        correlation_id: correlationId,
        source: 'communications_send',
      };
      const result = channel === 'sms'
        ? await previewOutboundSms(payload)
        : await previewOutboundEmail({
          ...payload,
          custom_subject: customSubject,
          confirm_bulk_send: true,
        });
      setPreviewData(result);
    } catch (error) {
      setPreviewData({
        status: 'failed',
        error: error.response?.data?.detail || 'Preview could not be generated.',
      });
    } finally {
      setPreviewLoading(false);
    }
  };

  // Auto-fetch preview when arriving at the final Review step so the operator
  // sees the rendered message inline without needing to click Preview.
  useEffect(() => {
    if (currentStep === 4 && canPreview && !previewData && !previewLoading) {
      handlePreview({ openModal: false });
    }
    // We intentionally exclude handlePreview and previewData identity from
    // deps — they aren't stable references but the conditions above gate
    // re-entry. eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, canPreview]);

  // Auto-confirm preview when it loads inline on Step 5 — the operator is
  // looking at the rendered preview right now; the redundant "Confirm
  // Preview" modal click adds nothing.
  //
  // IMPORTANT: This only unblocks the manual "Send to N recipients" button.
  // It does NOT trigger a send. The user must still click that button to
  // initiate dispatch. If the system is in live mode, an additional confirm
  // modal also gates the actual send. Auto-confirm here = preview-digest is
  // valid, nothing more.
  useEffect(() => {
    if (currentStep === 4 && previewData && !previewData.error && previewData.preview_digest && !previewConfirmed) {
      setPreviewConfirmed(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, previewData?.preview_digest]);

  const confirmPreview = () => {
    if (!previewData || previewData.error || !previewData.preview_digest) return;
    setPreviewConfirmed(true);
    setPreviewOpen(false);
  };

  const handleSubmit = async () => {
    if (submitBlockedReason || submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setSubmitResult(null);
    try {
      const result = channel === 'sms'
        ? await sendSms(templateId, selectedGuestIds, {
          custom_body: customBody,
          correlation_id: correlationId,
          preview_digest: previewData?.preview_digest,
          source: 'communications_send',
        })
        : await sendEmails(templateId, selectedGuestIds, [], [], true, {
          custom_subject: customSubject,
          custom_body: customBody,
          correlation_id: correlationId,
          preview_digest: previewData?.preview_digest,
          source: 'communications_send',
        });
      setSubmitResult({ type: result.status === 'failed' ? 'failed' : result.status, data: result });
    } catch (error) {
      const detail = error.response?.data?.detail || 'The communication could not be submitted.';
      const providerDisabled = error.response?.status === 503 || String(detail).toLowerCase().includes('provider');
      setSubmitResult({
        type: providerDisabled ? 'provider_disabled' : 'failed',
        data: { message: detail },
      });
    } finally {
      setLiveConfirmOpen(false);
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const requestSubmit = () => {
    if (submitBlockedReason || submittingRef.current) return;
    if (settings?.send_mode === 'live') {
      setLiveConfirmOpen(true);
      return;
    }
    handleSubmit();
  };

  const renderStep = () => {
    if (currentStep === 0) {
      return (
        <>
          <div className="communications-send-step__header">
            <span className="communications-step-label">Step 1 of 5</span>
            <h2>Choose the purpose of your message</h2>
            <p>Start with the intent so the rest of the workflow stays focused and easy to review.</p>
          </div>
          <div className="communications-send-list">
            {PURPOSES.map(item => (
              <SelectableCard key={item.key} item={item} selected={purpose === item.key} onSelect={handlePurposeSelect} />
            ))}
          </div>
        </>
      );
    }

    if (currentStep === 1) {
      return (
        <>
          <div className="communications-send-step__header">
            <span className="communications-step-label">Step 2 of 5</span>
            <h2>Choose the channel</h2>
            <p>Pick how this message will be delivered.</p>
          </div>
          <div className="communications-send-list communications-send-list--two">
            {CHANNELS.map(item => {
              const disabled = item.key === 'sms' && !smsAvailable;
              return (
                <SelectableCard
                  key={item.key}
                  item={item}
                  selected={channel === item.key}
                  disabled={disabled}
                  onSelect={handleChannelSelect}
                >
                  {disabled && <em>Provider disabled</em>}
                </SelectableCard>
              );
            })}
          </div>
        </>
      );
    }

    if (currentStep === 2) {
      return (
        <>
          <div className="communications-send-step__header">
            <span className="communications-step-label">Step 3 of 5</span>
            <h2>Select the template</h2>
            <p>Choose the message foundation, then adjust the content before preview.</p>
          </div>
          {templatesLoading ? (
            <div className="communications-empty"><p>Loading templates...</p></div>
          ) : templatesError ? (
            <div className="communications-send-warning"><AlertCircle size={18} /><span>{templatesError}</span></div>
          ) : !templatesForStep.length ? (
            <div className="communications-empty"><p>No eligible {channel === 'sms' ? 'SMS' : 'email'} templates are available.</p></div>
          ) : (
            <>
              <div className="communications-send-list">
                {templatesForStep.map(template => {
	                  const item = {
	                    key: getTemplateId(template),
	                    label: template.name || 'Untitled template',
	                    description: channel === 'sms' ? (template.message_text || 'No message text set') : (template.subject || 'No subject set'),
	                    icon: channel === 'sms' ? MessageSquare : Mail,
	                  };
                  return (
                    <SelectableCard
                      key={item.key}
                      item={item}
                      selected={templateId === item.key}
	                      onSelect={selectTemplate}
	                    >
	                      <span className="communications-send-card__chips">
	                        <em>{templateTypeLabel(template)}</em>
	                        {!template.is_active && <em>Inactive</em>}
	                      </span>
	                    </SelectableCard>
	                  );
	                })}
	              </div>
	              <div className="communications-send-composer">
                  {channel === 'email' && (
	                  <label>
	                    <span>Subject</span>
	                    <input value={customSubject} onChange={event => updateSubject(event.target.value)} />
	                  </label>
                  )}
	                <label>
	                  <span>{channel === 'sms' ? 'SMS Message' : 'Message Content'}</span>
	                  <textarea value={customBody} onChange={event => updateBody(event.target.value)} rows={8} />
	                </label>
	              </div>
            </>
          )}
        </>
      );
    }

    if (currentStep === 3) {
      return (
        <>
          <div className="communications-send-step__header">
            <span className="communications-step-label">Step 4 of 5</span>
            <h2>Select recipients</h2>
            <p>Select the exact guests who should receive this communication. RSVP Pending excludes guests who already answered and blocks repeated campaign recipients.</p>
          </div>
          {contextLabel && (
            <div className="communications-send-context">
              <UserRound size={18} />
              <span>{contextLabel}</span>
            </div>
          )}
          <div className="communications-audience-panel">
            <div>
              <strong>Audience Source</strong>
              <span>{audienceMode === 'rsvp_pending' ? 'RSVP Pending' : 'Manual selection'}</span>
            </div>
            <div className="communications-toolbar-actions">
              <button type="button" className="communications-button communications-button--ghost" onClick={() => {
                setAudienceMode('manual');
                setAudienceData(null);
                setAudienceError('');
                resetPreview();
              }}>
                Manual
              </button>
              <button type="button" className="communications-button communications-button--primary" onClick={loadRsvpPendingAudience} disabled={audienceLoading}>
                {audienceLoading ? 'Loading...' : 'Use RSVP Pending'}
              </button>
            </div>
          </div>
          {audienceData && (
            <div className="communications-audience-summary">
              <span><strong>{audienceData.counts?.eligible || 0}</strong> eligible</span>
              <span><strong>{audienceData.counts?.excluded || 0}</strong> excluded</span>
              <span><strong>{Object.keys(audienceData.counts?.reasons || {}).length}</strong> exclusion reason(s)</span>
            </div>
          )}
          {audienceError && (
            <div className="communications-send-warning communications-send-warning--compact">
              <AlertCircle size={18} />
              <span>{audienceError}</span>
            </div>
          )}
          <div className="communications-recipient-toolbar">
            <span className="communications-recipient-sort" aria-label="Recipients are sorted by Display Name">Display Name · A–Z</span>
            <label className="communications-search">
              <Search size={16} />
              <input
                value={recipientSearch}
                onChange={event => setRecipientSearch(event.target.value)}
                placeholder={`Search guests by name, ${channel === 'sms' ? 'phone' : 'email'}`}
              />
            </label>
            <button type="button" className="communications-button communications-button--ghost" onClick={clearRecipients}>
              Clear
            </button>
          </div>
          <div className="communications-recipient-list">
            {recipientRows.map(guest => {
              const guestId = getGuestId(guest);
              const checked = selectedGuestIds.includes(guestId);
              return (
                <label key={guestId} className={`communications-recipient-row ${checked ? 'communications-recipient-row--selected' : ''}`}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleGuest(guestId)}
                    disabled={audienceMode === 'rsvp_pending'}
                  />
                  <span>
                    <strong>{getGuestName(guest)}</strong>
                    <small>{channel === 'sms' ? guest.phone : guest.email}</small>
                  </span>
                </label>
              );
            })}
            {!recipientRows.length && (
              <div className="communications-empty"><p>No {channel === 'sms' ? 'SMS-ready' : 'email-ready'} guests match this search.</p></div>
            )}
          </div>
        </>
      );
    }

    return (
      <>
        <div className="communications-send-step__header">
          <span className="communications-step-label">Step 5 of 5</span>
          <h2>Review and send</h2>
          <p>This is what your recipients will receive.</p>
        </div>

        <div className="communications-send-review-chips">
          <span><strong>Channel</strong>{selectedChannel?.label}</span>
          <span><strong>Template</strong>{selectedTemplate?.name || '—'}</span>
          <span><strong>Recipients</strong>{selectedGuests.length}</span>
          {audienceMode === 'rsvp_pending' && <span><strong>Audience</strong>RSVP Pending</span>}
          {channel === 'sms' && Array.isArray(previewData?.media_urls) && previewData.media_urls.length > 0 && (
            <span><strong>Type</strong>MMS · {previewData.media_urls.length}</span>
          )}
        </div>

        <div className="communications-send-review-preview" data-testid="communications-send-step5-preview" ref={step5PreviewRef}>
          {previewLoading ? (
            <div className="communications-empty"><p>Preparing preview…</p></div>
          ) : previewData?.error ? (
            <div className="communications-send-warning">
              <AlertCircle size={18} />
              <span>{previewData.error}</span>
            </div>
          ) : previewData ? (
            channel === 'sms' ? (
              <div className="communications-sms-client communications-sms-client--desktop">
                <div className="communications-sms-bubble">
                  {previewData.text || customBody}
                </div>
                {Array.isArray(previewData.media_urls) && previewData.media_urls.length > 0 && (
                  <ul
                    style={{
                      listStyle: 'none',
                      margin: '8px 0 0',
                      padding: '8px 10px',
                      display: 'grid',
                      gap: 4,
                      borderRadius: 8,
                      border: '1px solid var(--border-light, rgba(198, 167, 90, 0.18))',
                      background: 'rgba(255, 255, 255, 0.48)',
                      font: 'inherit',
                      fontSize: 11,
                      color: 'var(--text-muted, #7A7367)',
                    }}
                  >
                    {previewData.media_urls.map((url, idx) => (
                      <li key={`${url}-${idx}`} title={url} style={{ display: 'flex', alignItems: 'center', gap: 6, overflow: 'hidden' }}>
                        <span aria-hidden="true">📎</span>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{url}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : (
              <div className="communications-email-client communications-email-client--desktop">
                <div className="communications-email-client__chrome">
                  <span />
                  <span />
                  <span />
                  <strong>{previewData.subject || customSubject}</strong>
                </div>
                <iframe title="Email preview" className="communications-preview-frame" srcDoc={previewData.html || ''} sandbox="" />
              </div>
            )
          ) : (
            <div className="communications-empty"><p>No preview yet. Select a template and audience.</p></div>
          )}
        </div>

        {submitBlockedReason && (
          <div className="communications-send-warning">
            <AlertCircle size={18} />
            <span>{submitBlockedReason}</span>
          </div>
        )}
        {submitResult && (
          <div className={`communications-send-result communications-send-result--${submitResult.type}`}>
            <ShieldCheck size={18} />
            <span>{submitResult.data?.message || 'Submission recorded.'}</span>
            {submitResult.data?.outbound_message_id && onOpenOutbox && (
              <button type="button" className="communications-button communications-button--ghost" onClick={onOpenOutbox}>
                View in Outbox
              </button>
            )}
          </div>
        )}
      </>
    );
  };

  return (
    <div className="communications-panel communications-send" data-testid="communications-send-panel">
      <div className="communications-stepper communications-send-stepper" aria-label="Send workflow">
        {STEPS.map((step, index) => (
          <button
            key={step.key}
            type="button"
            className={`communications-step ${index === currentStep ? 'communications-step--active' : ''} ${index < currentStep ? 'communications-step--complete' : ''}`}
            onClick={() => setCurrentStep(index)}
            aria-current={index === currentStep ? 'step' : undefined}
            data-testid={`communications-send-step-${step.key}`}
          >
            <span>{index < currentStep ? <CheckCircle2 size={16} /> : index + 1}</span>
            <strong>{step.label}</strong>
          </button>
        ))}
      </div>

      <div className="communications-workspace communications-send-workspace">
        {renderStep()}

        <div className="communications-send-actions">
          <button
            type="button"
            className="communications-button communications-button--ghost"
            onClick={goBack}
            disabled={currentStep === 0}
          >
            <ArrowLeft size={16} />
            Back
          </button>
          <div className="communications-send-actions__right">
            {/* Preview button shown only when all required fields are filled
                (canPreview = template + recipients + body). On Step 5 the
                inline preview auto-fetches and auto-confirms; this button
                opens the full-screen modal preview for users who want it. */}
            {canPreview && (
              <button
                type="button"
                className="communications-button communications-button--ghost"
                onClick={handlePreview}
                disabled={previewLoading}
                data-testid="communications-send-preview-button"
                title="Open the preview in a larger window"
              >
                <Eye size={16} />
                Preview
              </button>
            )}
            {currentStep < STEPS.length - 1 ? (
              <button
                type="button"
                className="communications-button communications-button--primary"
                onClick={goNext}
                data-testid="communications-send-next-button"
              >
                Next: {STEPS[currentStep + 1].label}
                <ArrowRight size={16} />
              </button>
            ) : (
              <button
                type="button"
                className={`communications-button ${submitBlockedReason ? 'communications-button--disabled' : 'communications-button--primary'}`}
                disabled={Boolean(submitBlockedReason)}
                onClick={requestSubmit}
                data-testid="communications-send-final-button"
              >
                <ShieldCheck size={16} />
                {finalButtonLabel}
              </button>
            )}
          </div>
        </div>
      </div>

      {previewOpen && (
        <div className="communications-confirm-backdrop" role="dialog" aria-modal="true" aria-label="Communication preview">
          <div className="communications-confirm communications-preview-modal communications-preview-modal--widescreen">
            <button
              type="button"
              className="communications-confirm__close"
              onClick={() => setPreviewOpen(false)}
              aria-label="Close preview"
            >
              <X size={16} />
            </button>
            <div className="communications-preview-modal__hero">
              <div>
	                <p className="communications-panel__eyebrow">Protected Preview</p>
	                <h3>Final {channel === 'sms' ? 'SMS' : 'Email'} Preview</h3>
	                <span>Review the exact rendered message before it moves forward.</span>
              </div>
              <div className="communications-preview-toggle">
                <button type="button" className={previewViewport === 'desktop' ? 'is-active' : ''} onClick={() => setPreviewViewport('desktop')}>
                  <Monitor size={15} />
                  Desktop
                </button>
                <button type="button" className={previewViewport === 'mobile' ? 'is-active' : ''} onClick={() => setPreviewViewport('mobile')}>
                  <Smartphone size={15} />
                  Mobile
                </button>
              </div>
            </div>
            {previewLoading ? (
              <div className="communications-preview-loading">
                <div />
                <p>Rendering email preview...</p>
              </div>
            ) : previewData?.error ? (
              <div className="communications-send-warning">
                <AlertCircle size={18} />
                <span>{previewData.error}</span>
              </div>
            ) : previewData ? (
              <>
                <div className="communications-preview-strip">
                  {channel === 'email' && <span><strong>Subject</strong>{previewData.subject || customSubject}</span>}
                  <span><strong>First recipient</strong>{previewData.first_recipient?.email || previewData.first_recipient?.phone || previewData.recipients?.[0]?.email || previewData.recipients?.[0]?.phone || '-'}</span>
                  <span><strong>Recipients</strong>{previewData.recipient_count} total / {previewData.sendable_count} sendable</span>
                  <span><strong>Mode</strong>{formatModeCompact(previewData.send_mode || settings?.send_mode)}</span>
                  {channel === 'sms' && Array.isArray(previewData.media_urls) && previewData.media_urls.length > 0 && (
                    <span><strong>Type</strong>MMS · {previewData.media_urls.length} attachment{previewData.media_urls.length === 1 ? '' : 's'}</span>
                  )}
                </div>
                {channel === 'sms' ? (
                  <div className={`communications-sms-client communications-sms-client--${previewViewport}`}>
                    <div className="communications-sms-bubble">
                      {previewData.text || customBody}
                    </div>
                    {Array.isArray(previewData.media_urls) && previewData.media_urls.length > 0 && (
                      <ul
                        data-testid="sms-preview-media"
                        style={{
                          listStyle: 'none',
                          margin: '8px 0 0',
                          padding: '8px 10px',
                          display: 'grid',
                          gap: 4,
                          borderRadius: 8,
                          border: '1px solid var(--border-light, rgba(198, 167, 90, 0.18))',
                          background: 'rgba(255, 255, 255, 0.48)',
                          font: 'inherit',
                          fontSize: 11,
                          color: 'var(--text-muted, #7A7367)',
                        }}
                      >
                        {previewData.media_urls.map((url, idx) => (
                          <li
                            key={`${url}-${idx}`}
                            title={url}
                            style={{ display: 'flex', alignItems: 'center', gap: 6, overflow: 'hidden' }}
                          >
                            <span aria-hidden="true">📎</span>
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{url}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ) : (
                  <div className={`communications-email-client communications-email-client--${previewViewport}`}>
                    <div className="communications-email-client__chrome">
                      <span />
                      <span />
                      <span />
                      <strong>{previewData.subject || customSubject}</strong>
                    </div>
                    <iframe title="Email preview" className="communications-preview-frame" srcDoc={previewData.html || ''} sandbox="" />
                  </div>
                )}
	                <div className="communications-email-client__footer">
	                  <span>{channel === 'sms' ? (previewData.text || customBody).slice(0, 140) : (stripHtml(previewData.html).slice(0, 120) || 'Rendered email preview')}</span>
	                </div>
	              </>
            ) : (
              <p>Preview is not available.</p>
            )}
            <div className="communications-confirm__actions">
              <button type="button" className="communications-button communications-button--ghost" onClick={() => setPreviewOpen(false)}>
                Close
              </button>
              <button
                type="button"
                className="communications-button communications-button--primary"
                onClick={confirmPreview}
                disabled={previewLoading || !previewData || Boolean(previewData.error) || !previewData.preview_digest}
                data-testid="communications-send-confirm-preview"
              >
                Confirm Preview
              </button>
            </div>
          </div>
        </div>
      )}

      {liveConfirmOpen && (
        <div className="communications-confirm-backdrop" role="dialog" aria-modal="true" aria-label="Confirm live send">
          <div className="communications-confirm communications-live-confirm">
            <button
              type="button"
              className="communications-confirm__close"
              onClick={() => setLiveConfirmOpen(false)}
              aria-label="Cancel live send"
            >
              <X size={16} />
            </button>
            <AlertCircle size={24} />
            <h3>Confirm Live Send</h3>
            <p>Live Send Enabled may contact real guests. Confirm the details before this message leaves the Hub.</p>
            <div className="communications-live-confirm__details">
              <SummaryRow label="Recipients" value={`${selectedGuests.length} selected`} />
              <SummaryRow label="Template" value={selectedTemplate?.name} />
              <SummaryRow label={channel === 'sms' ? 'Message' : 'Subject'} value={channel === 'sms' ? `${customBody.slice(0, 80)}${customBody.length > 80 ? '...' : ''}` : customSubject} />
              <SummaryRow label="Safety Mode" value={formatMode(settings?.send_mode)} />
            </div>
            <div className="communications-confirm__actions">
              <button type="button" className="communications-button communications-button--ghost" onClick={() => setLiveConfirmOpen(false)}>
                Cancel
              </button>
              <button type="button" className="communications-button communications-button--danger" onClick={handleSubmit} disabled={submitting}>
                Confirm Live Send
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="communications-send-count" aria-live="polite">
        <Users size={16} />
        <span>{selectedGuests.length} recipient(s) selected</span>
      </div>
    </div>
  );
}
